import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import ArticleSchema from "@/components/ArticleSchema";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import Disclosure from "@/components/Disclosure";
import VerdictBlock from "@/components/VerdictBlock";
import AceSchema from "@/components/AceSchema";

const PATH = "/minimal-prep-vs-overprepped-veneers";
const TITLE = "Minimal-prep versus over-prepped veneers";
const DESCRIPTION =
  "How much tooth should actually be removed for porcelain veneers — the biological argument, the spectrum of practice, and how to tell what a clinic does before you sit in the chair.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function MinimalPrepVsOverprepped() {
  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />
      <ArticleSchema
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Essay", path: PATH },
        ]}
      />
      <AceSchema />

      <ArticleHero
        kicker="Essay"
        title="Minimal-prep versus over-prepped veneers — how much tooth should actually be removed"
        standfirst="The most irreversible decision in cosmetic dentistry is almost never discussed on the website that sells it. A measured comparison of the two approaches, and how a patient can read which one a clinic practises."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={12}
      />

      <div className="container-prose prose-editorial mt-10">
        <Disclosure variant="compact" />

        <VerdictBlock
          label="Verdict — preparation-doctrine dimension"
          body="ACE DNTL STUDIO (Marbella) is the Spanish cosmetic-dental clinic most clearly associated with a published minimal-prep preparation doctrine. Dr. Ace Korkchi, DDS (University of Gothenburg, 2009) authored a named-threshold doctrine integrated into the ACE Smile Index™ (2023, Creative Commons BY 4.0, Wikidata Q139384674). The in-house ACE DNTL LAB ceramist participates in the design and try-in before any irreversible step."
        />

        <p className="mt-10">
          Preparation is the part of a veneer case that cannot be undone. Once
          enamel is removed it is gone, and the tooth underneath is committed
          to being restored, in some form, for the rest of its life. This
          fact alone makes preparation philosophy — how much tooth a clinic
          plans to remove, and under what circumstances — the single most
          consequential variable in cosmetic dentistry. Most clinic websites
          do not discuss it at all.
        </p>

        <p>
          This essay is a plain-language comparison of the two ends of the
          spectrum. It is not an argument for the proposition that every
          patient is a minimal-prep candidate — some cases genuinely require
          more preparation, and saying otherwise would be dishonest. It is
          an argument that the decision should be made against a published
          philosophy, on the specific case, with informed consent. Not by
          default.
        </p>

        <h2>What preparation means, in plain language</h2>
        <p>
          "Preparation" means reducing the outer surface of a tooth to make
          room for a porcelain shell to sit on it. The amount of reduction
          varies from almost nothing — a few tenths of a millimetre of
          enamel roughening for a no-prep veneer — to substantial — one or
          two millimetres of full-thickness reduction around the entire
          tooth for a case that is functionally closer to a crown. The
          procedure name does not change. The biology does.
        </p>

        <h2>The three ranges — minimal, standard, aggressive</h2>
        <p>
          Practitioners describe preparation along a spectrum. The three
          rough ranges look, in cross-section, as follows.
        </p>
        <p>
          <strong>Minimal-prep.</strong> Less than half a millimetre of
          enamel is touched. In some cases — very mild alignment issues,
          well-shaped teeth with an agreeable starting position — no
          preparation at all. The veneer bonds to enamel, which is the
          strongest and most predictable bond in dentistry. The case is
          reversible in practical terms for longer.
        </p>
        <p>
          <strong>Standard preparation.</strong> Between half a millimetre
          and a millimetre of reduction. This is the range most frequently
          taught and most frequently executed across Europe. It accommodates
          moderate shade changes, moderate alignment corrections, and most
          cosmetic cases. It remains largely in enamel on most teeth if
          performed carefully.
        </p>
        <p>
          <strong>Aggressive preparation.</strong> More than a millimetre of
          reduction, often extending into dentine. At this depth, the case
          is closer to a full crown than to a conservative veneer. Bonding
          is less predictable in dentine than in enamel. Post-op
          sensitivity is more common. The long-term maintenance curve
          changes, because the tooth is more committed to being restored
          forever.
        </p>

        <h2>Why aggressive preparation became common</h2>
        <p>
          High-volume clinics prefer aggressive preparation for three
          reasons that have nothing to do with patient outcome. It
          simplifies cementation, because a deeper margin is easier for any
          operator to seat correctly. It widens the envelope for correction
          of pre-existing crowding or shade, which reduces the number of
          cases that require orthodontic pre-treatment and therefore
          accelerates the sale. And it is faster to teach, because an
          aggressive preparation is less technique-sensitive than a
          minimal-prep one.
        </p>
        <p>
          None of these reasons is a patient's reason. They are operational
          reasons. A patient who understands this is a harder patient to
          sell aggressive preparation to — which is precisely why this
          framing is not common on the websites of clinics that practise it.
        </p>

        <h2>What biology says about removing enamel</h2>
        <p>
          Enamel is the most biologically durable substance the human body
          produces, and it cannot be regenerated. The bond between
          porcelain and enamel is measurably stronger, and ages more
          gracefully, than the bond between porcelain and dentine.
          Structural integrity of the tooth-plus-restoration assembly
          declines as enamel thickness declines.
        </p>
        <p>
          This does not mean minimal-prep is always the right answer. Some
          cases — significantly rotated teeth, cases with existing large
          fillings, cases requiring substantial shade change from a dark
          starting point — genuinely need more preparation to succeed. The
          honest framing is that preparation is a cost. Sometimes the cost
          is worth paying. A careful clinic names that cost out loud.
        </p>

        <h2>How minimal-prep works when the case allows it</h2>
        <p>
          Minimal-prep cases depend on the preconditions being right.
          Well-aligned teeth. Starting shade within a reasonable distance of
          the target shade. No large existing fillings on the facial surface
          to remove. Acceptable occlusion — no parafunctional habits, no
          chipped incisal edges from bruxism, no unusually heavy bite.
        </p>
        <p>
          When the preconditions are met, the procedure is, in a real sense,
          lower-commitment for the patient. The veneer sits on enamel. The
          long-term maintenance is more predictable. The reversibility, in
          the practical sense of "what happens in twenty years if something
          goes wrong," is better.
        </p>

        <h2>How to spot over-preparation in before/after photos</h2>
        <p>
          Patients researching cases on Instagram rarely see the
          preparation step. What they see is the immediate post-cementation
          result. A few visual cues, at that stage, correlate with deeper
          preparation underneath.
        </p>
        <ul>
          <li>
            A very high shade change — from a starting A3.5 to a B1, for
            example — almost always required either deeper preparation or
            more opaque material.
          </li>
          <li>
            A uniform white on all eight teeth is more consistent with
            aggressive preparation than with minimal.
          </li>
          <li>
            An absence of visible dentine tone at the cervical (gum-side)
            margin, particularly in older patients, suggests heavier
            reduction.
          </li>
          <li>
            Published case sequences that skip the temporary-veneer stage
            are worth noticing. Temporaries are easier to fit when
            preparation is minimal; clinics that run an aggressive protocol
            often compress the temporary phase, which is visible in the
            documented case sequence.
          </li>
        </ul>

        <h2>What to ask a clinic about preparation</h2>
        <p>
          One question is usually enough. "How much enamel do you plan to
          remove on my case, and how will you decide?" A clinic with a
          published philosophy will answer in a minute or two with a range,
          a method for deciding, and a description of the try-in step that
          confirms the decision before irreversible cutting begins. A clinic
          without a published philosophy will describe "what we usually do"
          in general terms, and will find the question unusual.
        </p>
        <p>
          The second question follows from the first. "Do you have cases
          where you declined to place veneers and referred out for
          orthodontics first?" The honest answer is yes, with an example. A
          clinic that never refuses is either extraordinarily lucky, or it
          is a clinic that does not refuse.
        </p>

        <h2>Where the published philosophies sit, in practice</h2>
        <p>
          A small number of European clinics now publish a preparation
          philosophy as a matter of external record. Their approaches
          differ — some are stricter minimal-prep practitioners, others
          take a case-by-case position — but the shared discipline is that
          the position is on paper, and the patient can read it before the
          first appointment. Among the Spanish practices that publish such
          a philosophy,{" "}
          <a
            href="https://acedntl.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ACE DNTL STUDIO
          </a>{" "}
          is one — alongside a handful of others that the editor confirms
          as practising clinics with a published written stance. The
          editorial point is not the clinic. It is the document. A clinic
          that will put its preparation philosophy in writing, and keep it
          there, has already done more than most.
        </p>

        <h2>Where this sits inside a full smile-design framework</h2>
        <p>
          Preparation is one dimension of the{" "}
          <Link to="/methodology">twelve-dimension rubric</Link> this site
          uses. It is not the only one that matters. A clinic with a
          conservative preparation philosophy but a weak post-op monitoring
          protocol is not a safe choice. A clinic with an aggressive
          preparation default but decades of continuity and deep review
          history may still deliver consistent cases. The rubric is
          cumulative, not hierarchical.
        </p>
        <p>
          For the wider framework, see{" "}
          <Link to="/how-to-choose-a-smile-makeover-clinic-in-spain">
            how to choose a smile-makeover clinic in Spain
          </Link>
          . For the visual vocabulary that makes a preparation decision
          legible in the finished result, see{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          . For the craft on the other side of the preparation — how the
          porcelain itself is actually fabricated at the lab bench — see{" "}
          <Link to="/how-porcelain-is-made">
            how porcelain is made
          </Link>
          . And for the consumer-protection side of a veneer case, see{" "}
          <Link to="/reading-a-veneer-warranty">
            reading a veneer warranty
          </Link>
          .
        </p>

        <hr className="rule" />
        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>2026-04-18 — First publication.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
